import { Calendar, MessageSquare } from "lucide-react";
import type { SubTask } from "@/features/tasks/types";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { PRIORITY_DOT, PRIORITY_LABELS } from "@/features/tasks/types";

interface Props {
  subtask: SubTask;
  onClick: () => void;
}

function weekChip(weeks: number | null) {
  if (weeks === null) return null;
  const text = weeks === 0 ? "0" : weeks > 0 ? `+${weeks}` : `${weeks}`;
  const cls =
    weeks < -1
      ? "text-red-700 font-semibold"
      : weeks === -1
      ? "text-red-500 font-medium"
      : weeks === 0
      ? "text-orange-600 font-semibold"
      : weeks === 1
      ? "text-yellow-600"
      : "text-gray-400 dark:text-gray-500";
  return <span className={`text-xs ${cls}`}>{text} tyg.</span>;
}

export function SubTaskBoardCard({ subtask, onClick }: Props) {
  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-[var(--color-primary)] cursor-pointer transition-all p-3 flex flex-col gap-2"
    >
      {/* Priority dot + title */}
      <div className="flex items-start gap-2">
        <span
          className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${PRIORITY_DOT[subtask.priority]}`}
          title={PRIORITY_LABELS[subtask.priority]}
        />
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug line-clamp-2">{subtask.title}</p>
      </div>

      {/* Parent task badge */}
      <span className="text-xs text-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full w-fit truncate max-w-full">
        {subtask.task_project_name
          ? `${subtask.task_project_name} · ${subtask.task_title}`
          : subtask.task_title}
      </span>

      {/* Footer */}
      <div className="flex items-center justify-between gap-1 mt-1">
        <div className="flex items-center gap-1 text-xs">
          {subtask.due_date && !["done", "cancelled", "n_d"].includes(subtask.status) && (
            <Calendar size={11} className="text-gray-400 dark:text-gray-500" />
          )}
          {!["done", "cancelled", "n_d"].includes(subtask.status) && weekChip(subtask.weeks_until_due)}
        </div>

        <div className="flex items-center gap-2">
          {subtask.comments_count > 0 && (
            <div className="flex items-center gap-0.5 text-xs text-gray-400 dark:text-gray-500">
              <MessageSquare size={11} />
              <span>{subtask.comments_count}</span>
            </div>
          )}
          {/* Assignee avatars */}
          <div className="flex -space-x-1.5">
            {subtask.assignees.slice(0, 3).map((a) => (
              <UserAvatar
                key={a.id}
                userId={a.id}
                fullName={a.full_name}
                profilePictureUrl={a.profile_picture_url}
                size="sm"
                border
              />
            ))}
            {subtask.assignees.length > 3 && (
              <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs flex items-center justify-center border border-white">
                +{subtask.assignees.length - 3}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
